import React, { createContext, useContext } from 'react';

import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

// A drag-and-drop row for Table (through its renderRow prop), and the handle that drags it.
//
// Rows are dragged by the handle only, never by the whole row: the row also holds a link, the up
// and down arrows and the Played switch, and a finger on a phone must still be able to scroll the
// page by touching anywhere but the grip. The handle can sit in any cell, so the row hands its drag
// bindings down through context rather than props, which Table's column renderers cannot receive.

interface HandleBindings {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

const SortableRowContext = createContext<HandleBindings | null>(null);

interface SortableRowProps {
  id: string;
  // A row that cannot move (a course switched off on this visit, or while a save is in flight).
  // It still takes part in the list, so the others can be dropped either side of it.
  disabled?: boolean;
  className: string;
  children: React.ReactNode;
}

export const SortableRow: React.FC<SortableRowProps> = ({
  id,
  disabled = false,
  className,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // Translate only, never scale: a table row that scales mid-drag squashes its cells.
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <SortableRowContext.Provider
      value={disabled ? null : { attributes, listeners, setActivatorNodeRef }}
    >
      <tr
        ref={setNodeRef}
        style={style}
        className={`${className} ${
          isDragging
            ? 'bg-white shadow-lg ring-1 ring-primary/50 dark:bg-charcoal'
            : ''
        }`}
      >
        {children}
      </tr>
    </SortableRowContext.Provider>
  );
};

// The grip. Renders nothing outside a SortableRow, or on a row that cannot move, so a cell can
// always include it and let the row decide.
export const DragHandle: React.FC<{ label: string }> = ({ label }) => {
  const bindings = useContext(SortableRowContext);
  if (bindings == null) {
    return null;
  }

  return (
    <button
      type="button"
      ref={bindings.setActivatorNodeRef}
      {...bindings.attributes}
      {...bindings.listeners}
      aria-label={label}
      title="Drag to reorder"
      // touch-none: without it a finger on the grip scrolls the page instead of lifting the row.
      className="flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-ink-muted transition-colors hover:bg-primary/10 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:cursor-grabbing dark:text-white/50 dark:hover:text-white"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
};
